import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Container,
} from '@mui/material';
import { Email, ArrowBack, CheckCircle } from '@mui/icons-material';
import AuthLayout from '../../components/auth/AuthLayout';
import { forgotPassword, clearError } from '../../redux/slices/authSlice';

// Validation schema
const ForgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
});

const ForgotPasswordPage = () => {
  const dispatch = useDispatch();
  const { isLoading, error } = useSelector((state) => state.auth);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (values, { resetForm }) => {
    const result = await dispatch(forgotPassword(values.email));
    if (result.meta.requestStatus === 'fulfilled') {
      setSuccess(true);
      resetForm();
    }
  };

  return (
    <AuthLayout>
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            py: 4,
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              textAlign: 'center',
              mb: 4,
              animation: 'fadeIn 0.6s ease-in',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'translateY(-20px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                mb: 2,
                boxShadow: '0 8px 16px rgba(102, 126, 234, 0.3)',
              }}
            >
              <Email sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              Forgot Password?
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ fontSize: '1.1rem', fontWeight: 400 }}
            >
              Don't worry, we'll help you reset it
            </Typography>
          </Box>

          <Card
            sx={{
              width: '100%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid rgba(0,0,0,0.08)',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 3 }}
                  onClose={() => dispatch(clearError())}
                >
                  {typeof error === 'object'
                    ? (error.detail || error.message || error.messages || JSON.stringify(error))
                    : error}
                </Alert>
              )}

              {success ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 3,
                    animation: 'fadeIn 0.5s ease-in',
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      backgroundColor: '#d4edda',
                      mb: 2,
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 40, color: '#28a745' }} />
                  </Box>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: 600, color: '#28a745', mb: 2 }}
                  >
                    Check Your Email
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 3 }}>
                    We've sent password reset instructions to your email address.
                    Please check your inbox and follow the link to reset your password.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Didn't receive the email? Check your spam folder or try again.
                  </Typography>
                  <Button
                    component={Link}
                    to="/auth/login"
                    variant="contained"
                    startIcon={<ArrowBack />}
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      px: 4,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5568d3 0%, #6a4193 100%)',
                        boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
                      },
                    }}
                  >
                    Back to Login
                  </Button>
                </Box>
              ) : (
                <>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    paragraph
                    sx={{ mb: 3, textAlign: 'center' }}
                  >
                    Enter your email address and we'll send you a link to reset your password.
                  </Typography>

                  <Formik
                    initialValues={{ email: '' }}
                    validationSchema={ForgotPasswordSchema}
                    onSubmit={handleSubmit}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      isSubmitting,
                    }) => (
                      <Form>
                        <TextField
                          fullWidth
                          id="email"
                          name="email"
                          label="Email Address"
                          variant="outlined"
                          margin="normal"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.email && Boolean(errors.email)}
                          helperText={touched.email && errors.email}
                          disabled={isLoading}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: 'primary.main',
                              },
                            },
                          }}
                        />

                        <Box sx={{ mt: 4, mb: 2 }}>
                          <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={isLoading}
                            sx={{
                              py: 1.5,
                              fontSize: '1rem',
                              fontWeight: 600,
                              textTransform: 'none',
                              borderRadius: 2,
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #5568d3 0%, #6a4193 100%)',
                                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
                                transform: 'translateY(-2px)',
                              },
                              '&:active': {
                                transform: 'translateY(0)',
                              },
                              '&:disabled': {
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                opacity: 0.7,
                              },
                            }}
                          >
                            {isLoading ? (
                              <CircularProgress size={24} color="inherit" />
                            ) : (
                              'Send Reset Link'
                            )}
                          </Button>
                        </Box>

                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                          <Link
                            to="/auth/login"
                            style={{ textDecoration: 'none' }}
                          >
                            <Typography
                              color="primary"
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                transition: 'opacity 0.2s',
                                '&:hover': {
                                  opacity: 0.8,
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              <ArrowBack fontSize="small" />
                              Back to Login
                            </Typography>
                          </Link>
                        </Box>
                      </Form>
                    )}
                  </Formik>
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      </Container>
    </AuthLayout>
  );
};

export default ForgotPasswordPage;